/**
 * PegasoLux Editorial Commerce System — client runtime.
 *
 * Two tiny custom elements, zero dependencies:
 *
 * `<editorial-reveal>` — wraps every editorial tile. Reveals it with a soft
 * rise the first time it enters the viewport. The hidden state is only ever
 * applied AFTER the element upgrades (via the data-reveal attribute), so a
 * visitor without JS — or a crawler — always sees the content. In the theme
 * editor and under prefers-reduced-motion the reveal is skipped entirely.
 *
 * `<editorial-video-flow>` — ambient campaign video. Sources are attached
 * lazily on first approach, playback runs only while the tile is on screen
 * (muted, looped, inline), and the video fades in over its poster once it is
 * actually playing, so there is never a black flash. Under
 * prefers-reduced-motion the video never loads; the poster stands alone.
 *
 * Both elements are re-entrant: Section Rendering API morphs (filters, sort,
 * theme editor changes) disconnect and reconnect them freely.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const designMode = window.Shopify && window.Shopify.designMode;

class EditorialReveal extends HTMLElement {
  static #observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-inview');
        EditorialReveal.#observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
  );

  connectedCallback() {
    if (designMode || reducedMotion.matches) {
      this.classList.add('is-inview');
      return;
    }

    // Tiles already inside the first viewport reveal without machinery —
    // the entrance is for content the visitor scrolls to, never for LCP.
    const rect = this.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      this.classList.add('is-inview');
      return;
    }

    this.setAttribute('data-reveal', '');
    EditorialReveal.#observer.observe(this);
  }

  disconnectedCallback() {
    EditorialReveal.#observer.unobserve(this);
  }
}

class EditorialVideoFlow extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #sourcesAttached = false;
  #inViewport = false;

  connectedCallback() {
    const video = this.querySelector('video');
    if (!video || reducedMotion.matches) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#inViewport = entry.isIntersecting;
          entry.isIntersecting ? this.#play(video) : video.pause();
        }
      },
      // Start warming the stream slightly before arrival; pause promptly on exit.
      { rootMargin: '200px 0px', threshold: 0.05 }
    );
    this.#observer.observe(this);

    video.addEventListener('playing', () => this.setAttribute('data-playing', ''), { once: true });
    document.addEventListener('visibilitychange', this.#onVisibilityChange, { passive: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  #onVisibilityChange = () => {
    const video = this.querySelector('video');
    if (!video) return;
    document.hidden ? video.pause() : this.#inViewport && this.#play(video);
  };

  /** @param {HTMLVideoElement} video */
  #play(video) {
    if (!this.#sourcesAttached) {
      for (const source of video.querySelectorAll('source[data-video-src]')) {
        source.setAttribute('src', source.getAttribute('data-video-src') || '');
      }
      video.load();
      this.#sourcesAttached = true;
    }

    video.muted = true;
    const attempt = video.play();
    if (attempt) attempt.catch(() => {});
  }
}

if (!customElements.get('editorial-reveal')) {
  customElements.define('editorial-reveal', EditorialReveal);
}

if (!customElements.get('editorial-video-flow')) {
  customElements.define('editorial-video-flow', EditorialVideoFlow);
}
